// src/app/stores/product.store.ts
import { Injectable, computed, signal } from '@angular/core';
import { AdminCommonHttpService } from '../components/admin/service/admin-common-http.service';
import { Product } from '../components/admin/models/combined-interfaces.model';

@Injectable({ providedIn: 'root' })
export class ProductStore {
  // --- raw state ---
  private readonly _products = signal<Product[]>([]);        // paged list
  private readonly _fullProducts = signal<Product[]>([]);    // full list for search
  private readonly _total = signal<number>(0);

  private readonly _pageSize = signal<number>(10);
  private readonly _currentPage = signal<number>(0);
  private readonly _sortField = signal<string>('created_at');
  private readonly _sortDirection = signal<'asc' | 'desc'>('desc');
  private readonly _searchText = signal<string>('');

  // --- public readonly signals ---
  readonly products = computed(() => this._products());
  readonly fullProducts = computed(() => this._fullProducts());
  readonly total = computed(() => this._total());

  readonly pageSize = computed(() => this._pageSize());
  readonly currentPage = computed(() => this._currentPage());
  readonly sortField = computed(() => this._sortField());
  readonly sortDirection = computed(() => this._sortDirection());
  readonly searchText = computed(() => this._searchText());

  // what the UI should render
  readonly filteredProducts = computed(() => {
    const search = this._searchText().trim().toLowerCase();

    if (search) {
      const full = this._fullProducts();
      return full.filter((p) =>
        (p.name ?? '').toLowerCase().includes(search) ||
        String(p.product_id ?? '').includes(search)
      );
    }

    return this._products();
  });

  constructor(private api: AdminCommonHttpService) { }

  // Call once on init
  async init(): Promise<void> {
    await Promise.all([
      this.reloadPaged(),
      this.reloadFull()
    ]);
  }

  // --- internal reload helpers ---

  private async reloadPaged(): Promise<void> {
    const size = this._pageSize();
    const offset = this._currentPage() * size;
    const sortField = this._sortField();
    const descending = this._sortDirection() === 'desc';

    const res = await this.api.getAllProducts(
      size,
      offset,
      sortField,
      descending
    );

    const products = res ?? [];
    this._products.set(products);
    this._total.set(products.length);
  }

  private async reloadFull(): Promise<void> {
    const res = await this.api.getAllProductsNoPagination();
    this._fullProducts.set(res ?? []);
  }

  private async reloadAll(): Promise<void> {
    await Promise.all([
      this.reloadPaged(),
      this.reloadFull()
    ]);
  }

  // --- pagination ---

  async setPageSize(size: number): Promise<void> {
    this._pageSize.set(size);
    this._currentPage.set(0);
    await this.reloadPaged();
  }

  async nextPage(): Promise<void> {
    this._currentPage.update((p) => p + 1);
    await this.reloadPaged();
  }

  async prevPage(): Promise<void> {
    this._currentPage.update((p) => (p > 0 ? p - 1 : 0));
    await this.reloadPaged();
  }

  // --- sorting ---

  async sortBy(field: string): Promise<void> {
    if (this._sortField() === field) {
      this._sortDirection.set(
        this._sortDirection() === 'asc' ? 'desc' : 'asc'
      );
    } else {
      this._sortField.set(field);
      this._sortDirection.set('asc');
    }

    await this.reloadPaged();
  }

  // --- search ---

  setSearch(text: string): void {
    this._searchText.set(text);
    // We rely on fullProducts already being loaded
  }

  // --- CRUD ---

  async addProduct(payload: Partial<Product>): Promise<void> {
    await this.api.addProduct(payload);
    await this.reloadAll();
  }

  async updateProduct(productId: number, updated: Partial<Product>): Promise<void> {
    await this.api.updateProduct(productId, updated);
    await this.reloadAll();
  }

  async deleteProduct(productId: number): Promise<void> {
    await this.api.deleteProduct(productId);
    await this.reloadAll();
  }

  async restoreProduct(productId: number): Promise<void> {
    await this.api.restoreProduct({ product_id: productId });
    await this.reloadAll();
  }
}
